/**
 * Envios ao Legado — vocabulário único da tela (spec 039).
 *
 * Mesma disciplina da `planReload.ts`: rótulo e cor são vocabulário do produto, não decisão de
 * tela. O público desta tela é operação/atendimento, então **nada de termo técnico** — o
 * `error_kind` do backend vira frase em pt-BR aqui, e o `http_status` fica restrito a Admin.
 */

export type LegacySendTone = "success" | "warning" | "error" | "neutral";

export interface LegacySendStatus {
  label: string;
  /** Cor da `Tag` do antd (nome de preset, não hex — dark mode depende disso). */
  color: string;
  tone: LegacySendTone;
}

/** Desfecho do envio de um orçamento ao legado (`outcome` do documento). */
const SEND_OUTCOME: Record<string, LegacySendStatus> = {
  success: { label: "Enviado", color: "green", tone: "success" },
  partial: { label: "Enviado com Falhas", color: "warning", tone: "warning" },
  error: { label: "Erro no Envio", color: "error", tone: "error" },
};

/** Desfecho de um item do envio (titular, dependente ou anexo). */
const ITEM_OUTCOME: Record<string, LegacySendStatus> = {
  sent: { label: "Aceito", color: "green", tone: "success" },
  refused: { label: "Recusado", color: "warning", tone: "warning" },
  failed: { label: "Falha no Envio", color: "error", tone: "error" },
};

/** Desfecho de uma execução do cron de reconciliação. */
const RUN_OUTCOME: Record<string, LegacySendStatus> = {
  success: { label: "Concluída", color: "green", tone: "success" },
  empty: {
    label: "Sem Orçamentos Elegíveis",
    color: "default",
    tone: "neutral",
  },
  partial: { label: "Concluída com Erros", color: "warning", tone: "warning" },
  error: { label: "Falha", color: "error", tone: "error" },
};

const UNKNOWN_STATUS: LegacySendStatus = {
  label: "—",
  color: "default",
  tone: "neutral",
};

/**
 * Valor desconhecido cai no rótulo cru (nunca em branco): o backend pode ganhar um desfecho novo
 * antes da tela, e é melhor exibir o código do que uma célula vazia.
 */
const resolve = (
  map: Record<string, LegacySendStatus>,
  value?: string | null,
): LegacySendStatus => {
  if (!value) {
    return UNKNOWN_STATUS;
  }
  return map[value] ?? { label: value, color: "default", tone: "neutral" };
};

export const sendOutcomeStatus = (value?: string | null): LegacySendStatus =>
  resolve(SEND_OUTCOME, value);

export const itemOutcomeStatus = (value?: string | null): LegacySendStatus =>
  resolve(ITEM_OUTCOME, value);

export const runOutcomeStatus = (value?: string | null): LegacySendStatus =>
  resolve(RUN_OUTCOME, value);

/** Opções do filtro de situação — aba de envios. */
export const SEND_OUTCOME_OPTIONS = [
  { label: "Todas", value: "" },
  { label: "Enviado", value: "success" },
  { label: "Enviado com Falhas", value: "partial" },
  { label: "Erro no Envio", value: "error" },
];

/** Opções do filtro de situação — aba de reconciliação. */
export const RUN_OUTCOME_OPTIONS = [
  { label: "Todas", value: "" },
  { label: "Concluída", value: "success" },
  { label: "Sem Orçamentos Elegíveis", value: "empty" },
  { label: "Concluída com Erros", value: "partial" },
  { label: "Falha", value: "error" },
];

/** Opções do filtro de tipo de item (busca em `items.target`). */
export const ITEM_TARGET_OPTIONS = [
  { label: "Todos", value: "" },
  { label: "Titular", value: "holder" },
  { label: "Dependente", value: "dependent" },
  { label: "Anexo", value: "attachment" },
];

/** Rótulo do beneficiário de um item. Dependente é numerado a partir de 1. */
export const beneficiaryLabel = (
  target?: string | null,
  index?: number | null,
): string => {
  if (target === "holder") {
    return "Titular";
  }
  if (target === "dependent") {
    return typeof index === "number" ? `Dependente ${index + 1}` : "Dependente";
  }
  if (target === "attachment") {
    return "Anexo";
  }
  return "—";
};

/**
 * Motivo da falha em pt-BR. O operador nunca lê `error_kind` nem código HTTP: essa tradução é o
 * que separa "Recusado pelo sistema legado" de um `http` cru na tela.
 */
const ERROR_KIND_MESSAGE: Record<string, string> = {
  http: "Recusado pelo sistema legado.",
  timeout: "O sistema legado não respondeu no tempo esperado.",
  connection: "Não foi possível conectar ao sistema legado.",
  empty_response: "O sistema legado respondeu sem conteúdo.",
  unexpected: "Erro inesperado no envio.",
};

export const errorKindMessage = (kind?: string | null): string | undefined => {
  if (!kind) {
    return undefined;
  }
  return ERROR_KIND_MESSAGE[kind] ?? "Erro inesperado no envio.";
};
